import React, {useEffect, useMemo, useState} from "react";
import {EmailHeader} from "../../services/messages/EmailHeaderDto";
import {EmailActions} from "../../actions/EmailActions";
import {isOutgoingFolder, SearchFolderId} from "../../reducers/FolderReducer";
import "./EmailHeaderView.scss";
import {AppState} from "../../AppState";
import {FolderTypeEnum} from '../../domain/Folder';
import {HotKeys} from 'react-hotkeys';
import {formatSubject} from "../../util/Formatters";
import {formatFromAddresses} from "./EmailHeaderGridColumns";
import {EmailHeaderStatusBar} from "./EmailHeaderStatusBar";
import {QuickFilterOptionList} from "../common/QuickFilterOption";
import {QuickFilterMode} from "../../actions/QuickFilterActionTypes";
import {useAppDispatch, useAppSelector, useAppStore} from "../../hooks/ReduxHooks";
import classNames from "classnames";
import {EmailLocation} from "../../locations/EmailLocation";
import {ContentLocation} from "../../locations/ContentLocation";
import {EmailContextMenu} from "./EmailContextMenu";
import {RelativeDateTime} from "../common/RelativeDateTime";
import {VerbIcon} from "./VerbIcon";
import {isTearOffWindow} from "../../App";
import NotificationsPausedIcon from "@mui/icons-material/NotificationsPaused";
import LockIcon from "@mui/icons-material/Lock";
import {SearchOptionsView} from "./SearchOptionsView";
import {KnownCategoriesMap} from "../../domain/KnownCategories";
import {EmptyEmailSearch} from "../common/EmptyEmailSearch";
import {ColDef, GridApi, GridOptions, RowSelectionOptions, SelectionChangedEvent} from "ag-grid-community";
import {AgGridReact, CustomCellRendererProps} from "ag-grid-react";
import type {CellContextMenuEvent} from "ag-grid-community/dist/types/src/events";
import {DefaultGridOptions, gridTheme, gridThemeDark, RelativeDateTimeRenderer} from "../../util/AgGridUtil";
import {CellClickedEvent, CellDoubleClickedEvent, GridReadyEvent, IRowNode, RowClickedEvent, RowDropZoneParams} from "ag-grid-enterprise";
import {FolderActions} from "../../actions/FolderActions";
import {pluralise} from "../../util/DateHelper";
import {isValidFolderForMovingEmail} from "../../util/FolderUtil";
import {LoadingIndicator} from "../common/LoadingIndicator";
import {Centre} from "../Centre";
import {DateTime} from "luxon";

interface EmailHeaderFolderProps {
    header: EmailHeader;
}

const ATTACHMENT_SVG = (
    <svg focusable="false" viewBox="0 0 24 24">
        <path
            d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z"/>
    </svg>
);

export const TrustShield = () => (
    <span className="icon from">
        <svg focusable="false" viewBox="0 0 24 24">
            <path
                d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
        </svg>
    </span>
);

function filterEmailHeaders(appState: AppState, filterMode: QuickFilterMode, searchText: string, categories: string[]) {
    const {folderState, itemState} = appState;

    const emailHeaders = folderState.selectedFolder?.type === FolderTypeEnum.Drafts
        ? itemState.draftEmailHeaders
        : itemState.emailHeaders;

    let filteredEmailHeaders = emailHeaders;
    switch (filterMode) {
        case "Unread":
            filteredEmailHeaders = emailHeaders.filter(h => !h.Read);
            break;
        case "Flagged":
            filteredEmailHeaders = emailHeaders.filter(h => h.Flagged || h.Pinned);
            break;
    }

    filteredEmailHeaders = categories.length > 0
        ? filteredEmailHeaders.filter(e => e.Category && categories.includes(e.Category))
        : filteredEmailHeaders;

    return searchText.length === 0
        ? filteredEmailHeaders
        : filteredEmailHeaders.filter(h =>
            h.From?.toLowerCase().includes(searchText)
            || h.Subject?.toLowerCase().includes(searchText)
            || h.To?.toLowerCase().includes(searchText)
            || h.Category?.toLowerCase().includes(searchText));
}

function RenderEmailFolderHeader({header}: EmailHeaderFolderProps) {
    const folder = useAppSelector(({folderState}) => folderState.folderList.find(f => f.folderId === header.FolderId));
    return <span className="header-folder">{folder?.name}</span>;
}

function getCategoryText(category: string | null) {
    if (!category) return category;
    return KnownCategoriesMap[category] ?? category;
}

const MobileRenderer = ({data: header, isOutgoing}: CustomCellRendererProps<EmailHeader> & { isOutgoing: boolean }) => {
    if (!header) return null;
    return (<>
        <div className="mobile-top-row">
            <div className="header-from">
                {formatFromAddresses(header, isOutgoing)}
                {header.ShowTrustShield && <TrustShield/>}
            </div>
            <div className="mobile-flags">
                {!!header.Pinned && <svg focusable="false" viewBox="0 0 24 24" className={classNames("header-flag", "icon", "flag", "pinned")}>
                    <path fillRule="evenodd"
                          d="M16 9V4h1c.55 0 1-.45 1-1s-.45-1-1-1H7c-.55 0-1 .45-1 1s.45 1 1 1h1v5c0 1.66-1.34 3-3 3v2h5.97v7l1 1 1-1v-7H19v-2c-1.66 0-3-1.34-3-3z"/>
                </svg>}
                {!!header.Flagged && <svg focusable="false" viewBox="0 0 24 24" className={classNames("header-flag", "icon", "flag", "flagged")}>
                    <path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z"/>
                </svg>}
            </div>
            <div className="header-date"><RelativeDateTime dateTime={header.Date} showPrefix={false}/></div>
        </div>
        <div className="mobile-bottom-row">
            <div className="header-subject">{formatSubject(header)}</div>
            {header.AttachmentCount > 0 && <div className="header-attachment">{ATTACHMENT_SVG}</div>}
            {header.Category && <div className={classNames("header-category", "email-category", "category-" + header.Category)}>{header.Category}</div>}
        </div>
    </>);
}

const AttachmentRenderer = ({data}: CustomCellRendererProps<EmailHeader>) => {
    if (!data) return null;
    return (data.AttachmentCount > 0
            ? ATTACHMENT_SVG
            : (data.Reminder
                ? <NotificationsPausedIcon style={{marginTop: "3px"}}/>
                : (data.MessageClass === "IPM.Note.SMIME" ? <LockIcon/> : <VerbIcon verb={data.LastVerb}/>))
    );
}

const SubjectRenderer = ({data, isSearchFolder}: CustomCellRendererProps<EmailHeader> & { isSearchFolder: boolean }) => {
    if (!data) return null;
    const displayCategory = getCategoryText(data.Category);
    return (<>
            {isSearchFolder && <RenderEmailFolderHeader header={data}/>}
            <span
                className={classNames("header-category", "email-category", "category-" + data.Category?.replace(" ", "_"), {"special-category": displayCategory !== data.Category})}
                title={data.Category ?? undefined}>
                    {displayCategory}
                </span>
            <span>{formatSubject(data)}</span>
        </>
    );
}

const FromRenderer = ({data, isOutgoing}: CustomCellRendererProps<EmailHeader> & { isOutgoing: boolean }) => {
    if (!data) return null;
    return (<>
            {formatFromAddresses(data, isOutgoing)}
            {data.ShowTrustShield && <TrustShield/>}
        </>
    );
}

const FlagsRenderer = ({data}: CustomCellRendererProps<EmailHeader>) => {
    if (!data) return null;

    if (data.Pinned) {
        return (
            <svg focusable="false"
                 viewBox="0 0 24 24"
                 className={classNames("header-flag", "icon", "flag", "pinned")}
                 style={{position: "static"}}>
                <path fillRule="evenodd" d="M16 9V4h1c.55 0 1-.45 1-1s-.45-1-1-1H7c-.55 0-1 .45-1 1s.45 1 1 1h1v5c0 1.66-1.34 3-3 3v2h5.97v7l1 1 1-1v-7H19v-2c-1.66 0-3-1.34-3-3z"/>
            </svg>
        );
    }
    if (data.Flagged) {
        return (
            <svg focusable="false"
                 viewBox="0 0 24 24"
                 className={classNames("header-flag", "icon", "flag", "flagged")}>
                <path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z"/>
            </svg>
        );
    }
    return null;
}

// noinspection JSUnusedGlobalSymbols
const getGridOptions = (isMobile: boolean, isSearchFolder: boolean): GridOptions<EmailHeader> => ({
    ...DefaultGridOptions,
    getRowId: params => params.data.LocalId ?? params.data.Id.toString(),
    rowClassRules: {
        "unread": params => params.data?.Read === 0,
        "important": params => params.data?.Importance === "High",
    },
    isGroupOpenByDefault: params => isMobile ? params.key !== "Pinned" : true,
    headerHeight: isMobile ? 0 : undefined,
    ...(isMobile
            ? (isSearchFolder ? {rowHeight: 60} : {getRowHeight: params => params.data ? 60 : 50})
            : {rowHeight: 30}
    ),
    rowClass: isMobile ? "mobile" : undefined,

    suppressRowDrag: isMobile,
    rowDragEntireRow: !isMobile,
    rowDragManaged: false,
    suppressMoveWhenRowDragging: true,
    rowDragMultiRow: !isMobile,
    rowDragText: params => "Move " + pluralise(params.rowNodes?.length ?? 1, "email", true),
});

const getColumnDefs = (isOutgoing: boolean, isSearchFolder: boolean): ColDef[] => ([
    {
        field: "DateRangeCategory",
        rowGroup: !isSearchFolder,
        hide: true,
    },
    {
        width: 30,
        field: "AttachmentCount",
        headerName: " ",
        cellClass: "header-attachment",
        cellRenderer: AttachmentRenderer,
    },
    {
        flex: 1,
        // rowGroup: true,
        headerName: isOutgoing ? "To" : "From",
        cellClass: "header-from",
        cellClassRules: {
            "trust-shield": params => !!params.data?.ShowTrustShield,
        },
        // valueGetter: params => isOutgoing ? params.data?.To : params.data?.From,
        cellRenderer: FromRenderer,
        cellRendererParams: {isOutgoing},
    },
    {
        width: 30,
        cellRenderer: FlagsRenderer,
    },
    {
        field: "Subject",
        flex: 2,
        cellClass: "header-subject",
        cellRenderer: SubjectRenderer,
        cellRendererParams: {isSearchFolder},
    },
    {
        hide: true,
        aggFunc: "sum",
        sort: "desc",
        sortIndex: 0,
        suppressMovable: true,
        valueGetter: params => params.data?.Pinned ? 1 : 0,
    },
    {
        cellClass: "header-date",
        headerName: "Date",
        width: 110,
        aggFunc: "max",
        sort: "desc",
        sortIndex: 1,
        suppressMovable: true,
        valueGetter: params => params.data?.DisplayDate ?? params.data?.Date,
        cellRenderer: RelativeDateTimeRenderer,
    },
]);


const getMobileColumnDefs = (isOutgoing: boolean, isSearchFolder: boolean): ColDef[] => ([
    {
        field: "DateRangeCategory",
        rowGroup: !isSearchFolder,
        hide: true,
    },
    {
        flex: 1,
        cellRenderer: MobileRenderer,
        cellRendererParams: {isOutgoing},
    },
    {
        hide: true,
        aggFunc: "sum",
        sort: "desc",
        sortIndex: 0,
        suppressMovable: true,
        valueGetter: params => params.data?.Pinned ? 1 : 0,
    },
    {
        field: "Date",
        hide: true,
        aggFunc: "max",
        sort: "desc",
        sortIndex: 1,
        suppressMovable: true,
    },
]);


export const EmailHeaderView = () => {
    const store = useAppStore();
    const dispatch = useAppDispatch();

    const [gridApi, setGridApi] = useState<GridApi<EmailHeader> | null>(null);
    const [gridEl, setGridEl] = useState<Element | null>(null);

    const isDarkTheme = useAppSelector(({system}) => system.theme === "dark");
    const isMobile = useAppSelector(({system}) => system.isMobile);

    const selectedCategories = useAppSelector(s => s.itemState.selectedCategoriesFilter);

    const [searchText, setSearchText] = useState<string>("");
    const [filterMode, setFilterMode] = useState<QuickFilterMode>("All");

    const [previousRowIndex, setPreviousRowIndex] = useState<number | undefined>(undefined);

    const lazyLoadDataSource = useAppSelector(({folderState}) => folderState.lazyLoadDataSource);
    const isSearchResults = useAppSelector(s => s.folderState.selectedFolderId === SearchFolderId);

    const emailHeaders = useAppSelector(state =>
        lazyLoadDataSource
            ? state.itemState.emailHeaders
            : filterEmailHeaders(state, filterMode, searchText, selectedCategories));

    const selectedItemSelector = ({itemState}: AppState) => itemState.selectedEmailHeaders;
    const selectedEmailHeaders = useAppSelector(selectedItemSelector);

    const mobileSelectionEnabled = useAppSelector(s => s.itemState.mobileSelectionEnabled);

    const selectedFolderType = useAppSelector(({folderState}) => folderState.selectedFolder?.type);

    const isOutgoing = isOutgoingFolder(selectedFolderType);

    const folderLoadingInProgress = useAppSelector(state => state.itemState.folderLoadingInProgress);

    useEffect(() => {
        // Update the selection of the grid to match what we have in memory
        if (gridApi && !gridApi?.isDestroyed()) {
            const selectedIds = new Set<number>(selectedEmailHeaders.map(s => s.Id));
            const selectedNodes = gridApi.getSelectedNodes();
            gridApi?.startBatchEdit();
            for (const node of selectedNodes) {
                const id = node.data?.Id;
                if (id !== undefined) {
                    if (!selectedIds.has(id)) {
                        node.setSelected(false, false, "api");
                        selectedIds.delete(id);
                    }
                }
            }
            for (const id of selectedIds) {
                gridApi.getRowNode("" + id)?.setSelected(true, false, "api");
            }
            gridApi?.commitBatchEdit();
        }
    }, [gridApi, selectedEmailHeaders]);

    const columnDefs = useMemo(
        () => isMobile
            ? getMobileColumnDefs(isOutgoing, isSearchResults)
            : getColumnDefs(isOutgoing, isSearchResults)
        , [isMobile, isOutgoing, isSearchResults]);

    const gridOptions = getGridOptions(isMobile, !!lazyLoadDataSource);

    const rowSelection: RowSelectionOptions<EmailHeader> | undefined = {
        mode: isMobile && !mobileSelectionEnabled ? "singleRow" : "multiRow",
        selectAll: "filtered",
        checkboxes: !isMobile || mobileSelectionEnabled,
        headerCheckbox: !lazyLoadDataSource,
        groupSelects: "filteredDescendants",
        checkboxLocation: 'autoGroupColumn',
        // enableSelectionWithoutKeys: true,
        isRowSelectable: node => !!node.data,
    };

    if (isTearOffWindow) return null;

    console.log("Render email header view in", lazyLoadDataSource ? "virtual" : "client", "mode");

    function handleSelectQuickFilterMode(newMode: QuickFilterMode) {
        setFilterMode(newMode);
    }

    function handleQuickSearchTextChange(searchText: string) {
        setSearchText(searchText.toLowerCase());
    }

    function onUpdateSelection(newSelection: EmailHeader[]) {
        dispatch(EmailActions.selectEmailHeaders(newSelection));
        if (newSelection.length === 0) {
            ContentLocation.clearItemContent();
        }
    }

    function openEmailHeader(header: EmailHeader, newSelection: EmailHeader[]) {
        if (header.IsDraft) {
            EmailLocation.openDraftEmail(header);
        } else if (newSelection.length === 1 && !mobileSelectionEnabled) {
            ContentLocation.selectEmailHeader(header);
        }
    }

    // noinspection JSUnusedGlobalSymbols
    const keyHandlers = {
        DELETE: () => EmailActions.deleteSelectedEmails(dispatch, store.getState()),
        UP: () => {
            const selection = gridApi?.getSelectedNodes();
            if (selection?.length === 1) {
                const index = selection[0]?.rowIndex ?? -1;
                let selectedNode = gridApi?.getDisplayedRowAtIndex(index - 1);
                if (selectedNode?.groupData) {
                    selectedNode = gridApi?.getDisplayedRowAtIndex(index - 2);  // For row groups
                }
                selectedNode?.setSelected(true, true, "rowClicked");
            }
        },
        DOWN: () => {
            const selection = gridApi?.getSelectedNodes();
            if (selection?.length === 1) {
                const index = selection[0]?.rowIndex ?? NaN;
                let selectedNode = gridApi?.getDisplayedRowAtIndex(index + 1);
                if (selectedNode?.groupData) {
                    selectedNode = gridApi?.getDisplayedRowAtIndex(index + 2);  // For row groups
                }
                selectedNode?.setSelected(true, true, "rowClicked");
            }
        }
    };

    function handleGridReady(params: GridReadyEvent<EmailHeader>) {
        setGridApi(params.api);

        const folderTreePane = document.querySelector(".folder-tree-pane");
        if (!folderTreePane) return;

        const finishDrop = () => {
            dispatch(FolderActions.cancelMoveFolder());
            dispatch(FolderActions.setContextFolder(undefined));
            // folderTreePane.className = folderTreePane.className.replace(" drag-target", "");
        }

        const getFolderFromEvent = (event: { pageX: number, pageY: number }) => {
            const hoverElements = document.elementsFromPoint(event.pageX, event.pageY);
            for (let i = hoverElements.length - 1; i >= 0; i--) {
                const element = hoverElements[i];
                if (element.id?.startsWith("folder-")) {
                    const folderId = parseInt(element.id.substring(7));
                    const {folderState} = store.getState();
                    const folder = folderState.folderList.find(f => f.folderId === folderId);
                    if (folder && isValidFolderForMovingEmail(folder) && folder.folderId !== folderState.selectedFolderId) {
                        return folder;
                    }
                    break;
                }
            }
            return undefined;
        };

        const dropZone: RowDropZoneParams = {
            getContainer: () => folderTreePane as HTMLElement,
            onDragEnter: (params) => {
                dispatch(EmailActions.beginMoveEmails(params.overNode?.data));
            },
            onDragging: (params) => {
                const folder = getFolderFromEvent(params.event);

                dispatch(FolderActions.setContextFolder(folder));
            },
            onDragStop: (params) => {
                const folder = getFolderFromEvent(params.event);
                finishDrop();
                if (folder) {
                    const emails = params.nodes.map((n: IRowNode<EmailHeader>) => n.data!).filter(n => !!n);
                    EmailActions.completeMoveEmails(dispatch, emails, folder);
                }
            },
            onDragCancel: () => finishDrop(),
            onDragLeave: () => finishDrop(),
        };
        params.api.addRowDropZone(dropZone);
    }

    function handleRowClicked(params: RowClickedEvent<EmailHeader>) {
        if (!params.data) {
            params.api.setRowNodeExpanded(params.node, !params.node.expanded);
        }
    }

    function handleCellDoubleClicked(params: CellDoubleClickedEvent<EmailHeader>) {
        window.open(window.location.href, "_blank", "popup=true");
    }

    function handleCellClicked({event, node, api, rowIndex, data}: CellClickedEvent<EmailHeader>) {
        if (!data) {
            return;
        }
        const mouseEvent = event as any;
        if (mouseEvent?.ctrlKey || selectedEmailHeaders.length > 1 || mobileSelectionEnabled) {
            node.setSelected(!node.isSelected(), false, "rowClicked");
        } else if (mouseEvent?.shiftKey && api.getSelectedNodes().length > 0) {
            // const rowIndex = params.rowIndex;
            if (previousRowIndex !== undefined && rowIndex !== null) {
                const startRowIndex = previousRowIndex > rowIndex ? rowIndex : previousRowIndex;
                const endRowIndex = previousRowIndex < rowIndex ? rowIndex : previousRowIndex;
                console.log(startRowIndex, endRowIndex);
                api.startBatchEdit();
                api.forEachNodeAfterFilter(node => {
                    if (!node.rowIndex || !node.data) return;
                    if (node.rowIndex >= startRowIndex && node.rowIndex <= endRowIndex) {
                        node.setSelected(true, false, "checkboxSelected");
                    }
                });
                api.commitBatchEdit();
            }
        } else {
            if (api.getSelectedRows().length === 1 && node.isSelected()) {
                openEmailHeader(data, api.getSelectedRows());
            } else {
                node.setSelected(true, true, "rowClicked");
            }
        }
        if (rowIndex !== null) {
            setPreviousRowIndex(rowIndex);
        }
        if (isOutgoing || isMobile) {
            openEmailHeader(data, api.getSelectedRows());
        }
    }

    function handleSelectionChanged(params: SelectionChangedEvent<EmailHeader>) {
        if (params.source === "api") {
            return;
        }
        const newSelection = params.api.getSelectedRows();
        onUpdateSelection(newSelection);

        if (newSelection.length === 1 && !isOutgoing) {
            openEmailHeader(newSelection[0], newSelection);
        }
    }

    function handleCellContextMenu(event: CellContextMenuEvent<EmailHeader>) {
        if (event.data) {
            onUpdateSelection([event.data]);
            if (isMobile) {
                dispatch(EmailActions.setMobileSelectionEnabled(true));
            } else {
                setGridEl(event.event?.target as Element);
            }
        }
        event.event?.preventDefault();
    }

    // TODO: Use the SSRM instead of infinite for when we're searching
    return (
        <HotKeys className="email-view content-item-list" handlers={keyHandlers}>
            {!lazyLoadDataSource && (<QuickFilterOptionList onSelectQuickFilterMode={handleSelectQuickFilterMode}
                                                            onQuickSearchTextChange={handleQuickSearchTextChange}/>)}
            {lazyLoadDataSource && isSearchResults && <SearchOptionsView/>}

            <EmailHeaderStatusBar/>

            <AgGridReact<EmailHeader> key={(lazyLoadDataSource ? "virtual" : "client") + isMobile + DateTime.local().day}
                                      theme={isDarkTheme ? gridThemeDark : gridTheme}
                                      gridOptions={gridOptions}
                                      rowSelection={rowSelection}
                                      columnDefs={columnDefs}
                                      rowModelType={lazyLoadDataSource ? "infinite" : "clientSide"}
                                      rowData={lazyLoadDataSource ? undefined : emailHeaders}
                                      datasource={lazyLoadDataSource ? lazyLoadDataSource : undefined}
                                      onSelectionChanged={handleSelectionChanged}
                                      onGridReady={handleGridReady}
                                      onCellContextMenu={handleCellContextMenu}
                                      preventDefaultOnContextMenu={true}
                                      onCellClicked={handleCellClicked}
                                      onCellDoubleClicked={handleCellDoubleClicked}
                                      onRowClicked={handleRowClicked}
                                      noRowsOverlayComponent={() => isSearchResults ?
                                          <EmptyEmailSearch/> : (searchText ? "No emails found for current filter" : "No emails in this folder")}
            />

            {folderLoadingInProgress && <Centre className="fill-container"><LoadingIndicator/></Centre>}

            {!isMobile && <EmailContextMenu anchorEl={gridEl} onClose={() => setGridEl(null)}/>}
        </HotKeys>
    );
};
